import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import EventTypesTemplate from "templates/events/eventTypes";
import {
  activateEventTypeRequest,
  createEventTypeRequest,
  deactivateEventTypeRequest,
  deleteEventTypeRequest,
  getEventTypeDetailsRequest,
  getEventTypesRequest,
  updateEventTypeRequest,
} from "services/modules/events/eventTypes";

function EventTypesPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  let [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const list = useSelector((state) => state.eventTypes.eventTypes);
  const count = useSelector((state) => state.eventTypes.count);
  const details = useSelector((state) => state.eventTypes.eventType);
  const load = useSelector((state) => state.eventTypes.load);

  const currentUser = useSelector(
    (state) => state.authenticate.currentUserProfile,
  );
  const permissions = useSelector(
    (state) => state.authenticate.currentUserProfile.permissions?.event_types,
  );
  const isSuperAdmin = currentUser?.privilege == "super_admin";

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [page, setPage] = useState(1);
  const [filterObj, setFilterObj] = useState(null);

  const handlePageChange = (page) => {
    setPage(page);
    setFilterObj((prev) => {
      return { ...prev, page_number: page };
    });
  };

  const getDetails = async () => {
    dispatch(getEventTypeDetailsRequest({ id }));
  };

  const getList = async (filterObj) => {
    const requestData = {
      params: {
        page_size: 10,
        page_number: 1,
        keyword: "",
        sort_by: "id",
        ...filterObj,
      },
    };
    dispatch(getEventTypesRequest(requestData));
  };

  const mappedData = list?.map((data) => {
    const nameEn =
      data?.localized_data?.en?.name ||
      data?.localized_data?.name_en ||
      data?.name_en ||
      data?.name ||
      "";
    const nameAr =
      data?.localized_data?.ar?.name ||
      data?.localized_data?.name_ar ||
      data?.name_ar ||
      "";
    const imageSrc = data?.image;

    return {
      ...data,
      name: nameEn,
      name_en: nameEn,
      name_ar: nameAr,
      created_at: <>{dayjs(data?.created_at).format("DD-MM-YYYY hh:mm")}</>,
      display_priority: data?.display_priority ?? "-",
      image_url: imageSrc ? (
        <img src={imageSrc} alt={nameEn} width="125px" height="77px" />
      ) : (
        t("chat.no_image")
      ),
      is_active_label: (
        <>{data?.is_active ? t("common.yes") : t("common.no")}</>
      ),
    };
  });

  const rowData = () => {};

  const breadcrumbs = [
    {
      active: true,
      path: "/",
      title: t("common.home"),
    },
    {
      active: !type ? false : true,
      path: "/events/event-types",
      title: t("events_event_types.title"),
    },
    type && {
      active: false,
      title:
        type === "details"
          ? t("events_event_types.details")
          : id
            ? t("events_event_types.edit")
            : t("events_event_types.add"),
    },
  ];

  const handelSubmit = (data, formik) => {
    const action = () => {
      searchParams.delete("id");
      searchParams.delete("type");
      navigate("/events/event-types");
      getList(filterObj);
      !id && formik?.resetForm();
      toast.success(
        id
          ? t("events_event_types.updated")
          : t("events_event_types.created"),
      );
    };
    const requestData = {
      body: {
        event_type: {
          name_en: formik?.values?.name_en,
          name_ar: formik?.values?.name_ar,
          image: data?.image,
          display_priority: formik?.values?.display_priority
            ? Number(formik?.values?.display_priority)
            : 0,
          is_active: !!formik?.values?.is_active,
        },
      },
      action,
    };

    if (id) {
      dispatch(updateEventTypeRequest({ ...requestData, id }));
    } else {
      dispatch(createEventTypeRequest(requestData));
    }
  };

  const filterList = [
    {
      type: "filter",
      placeholder: t("events_event_types.is_active"),
      onChange: (value) => {
        setFilterObj((s) => {
          return { ...s, is_active: value?.value, page_number: 1 };
        });
      },
      options: [
        { id: true, name: t("common.yes") },
        { id: false, name: t("common.no") },
      ],
    },
  ];

  const handleDelete = (deleteId) => {
    const action = () => {
      if ((list.length === 1) & (page !== 1)) {
        setPage(page - 1);
        setFilterObj({
          ...filterObj,
          page_number: page - 1,
        });
        getList({ ...filterObj, page_number: page - 1 });
      } else {
        getList(filterObj);
      }
      type === "details" && navigate("/events/event-types");
      toast.success(t("events_event_types.deleted"));
    };
    dispatch(deleteEventTypeRequest({ id: deleteId, action }));
    setOpenDeleteModal(false);
  };

  const handleToggleActive = (data) => {
    const isActive = data?.is_active === true;
    const action = () => {
      toast.success(
        isActive
          ? t("events_event_types.deactivated")
          : t("events_event_types.activated"),
      );
      getList(filterObj);
    };
    const requestData = { id: data?.id, action };
    if (isActive) {
      dispatch(deactivateEventTypeRequest(requestData));
    } else {
      dispatch(activateEventTypeRequest(requestData));
    }
  };

  const actionsList = ({ data }) => {
    const isActive = data?.is_active === true;
    return [
      {
        condition: () => true,
        click: () => {
          navigate(`?type=details&id=${data?.id}`);
        },
        text: t("common.details"),
      },
      {
        condition: () => isSuperAdmin || permissions?.can_update,
        click: () => {
          navigate(`?type=form&id=${data?.id}`);
        },
        text: t("common.edit"),
      },
      {
        condition: () => isSuperAdmin || permissions?.can_update,
        click: () => {
          handleToggleActive(data);
        },
        text: isActive
          ? t("events_event_types.deactivate")
          : t("events_event_types.activate"),
      },
      {
        condition: () => isSuperAdmin || permissions?.can_delete,
        click: () => {
          setOpenDeleteModal(true);
        },
        text: t("common.delete"),
      },
    ];
  };

  useEffect(() => {
    if (filterObj) getList(filterObj);
  }, [filterObj]);

  useEffect(() => {
    if (id) getDetails();
  }, [id]);

  return (
    <>
      <Loader open={load?.loading && load?.type == "list"} />
      <EventTypesTemplate
        filterObj={filterObj}
        setFilterObj={setFilterObj}
        data={mappedData}
        count={count}
        paginationPage={page}
        handlePageChange={handlePageChange}
        rowData={rowData}
        details={details}
        breadcrumbs={breadcrumbs}
        filterList={filterList}
        handelSubmit={handelSubmit}
        actionsList={actionsList}
        headerChildren={<></>}
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        handleDeleteRequest={handleDelete}
        modalComponents={[]}
        can_create={isSuperAdmin || permissions?.can_create}
        can_update={isSuperAdmin || permissions?.can_update}
      />
    </>
  );
}

export default EventTypesPage;
